/**
 * SystemSettings - Super Admin control panel for SMTP, SMS, and integrations
 * Only accessible by users with super_admin role
 */
import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { SiteAppearancePanel } from '@/pages/AdminHeroImages';
import { toast } from 'sonner';
import { uploadMedia } from '@/lib/mediaUpload';
import {
  Settings, Mail, MessageSquare, Key, Shield, Server,
  Loader2, Save, TestTube, CheckCircle, XCircle, Eye, EyeOff,
  Smartphone, CreditCard, Brain, Globe, RefreshCw, Send, MessageCircle,
  Upload, Image, X, MailCheck, AlertCircle, Video, Palette, Layout, Play
} from 'lucide-react';
import api, { systemAPI, getErrorMessage } from '@/lib/api';
import CurrencyManager from './CurrencyManager';
import CurrencySettings from './admin/CurrencySettings';
import LandingPagesSettings from './LandingPagesSettings';
import ModuleVisibilityManager from './ModuleVisibilityManager';
import HierarchicalRoleAssignment from './HierarchicalRoleAssignment';
import HierarchicalFinanceDashboard from './HierarchicalFinanceDashboard';
import EmailOutboxCard from './settings/EmailOutboxCard';

// SecretInput component for masked password fields - extracted to avoid re-renders
const SecretInput = ({ field, value, placeholder, showSecrets, onToggle, onChange }) => (
  <div className="relative">
    <Input
      type={showSecrets[field] ? 'text' : 'password'}
      value={value || ''}
      onChange={(e) => onChange(field, e.target.value)}
      placeholder={placeholder}
      className="pr-10"
    />
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
      onClick={() => onToggle(field)}
    >
      {showSecrets[field] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
    </Button>
  </div>
);

// FileUpload component for logo, favicon, images, and videos
const FileUpload = ({ label, value, onChange, fileType, accept, previewSize = "h-12" }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  // Legacy `fileType` names line up with the media pipeline's purposes.
  const PURPOSES = { logo: 'logo', favicon: 'favicon', hero: 'hero',
                     background: 'banner', event: 'cover', general: 'general' };

  const handleFileSelect = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const asset = await uploadMedia(file, { purpose: PURPOSES[fileType] || 'general' });
      // Store the RELATIVE path so the same record works across preview and
      // production; the browser resolves it against the current origin.
      onChange(asset.url);
      toast.success(`${asset.filename} uploaded`);
    } catch (error) {
      toast.error(error.message || 'That file could not be uploaded');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileSelect(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const isVideoUpload = fileType === 'video' || (accept || '').includes('video');

  const handleClear = () => {
    onChange('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      
      {/* Drag and Drop Zone */}
      <div
        className={`
          border-2 border-dashed rounded-lg p-4 transition-all cursor-pointer
          ${dragOver ? 'border-indigo-500 bg-indigo-50' : 'border-slate-300 hover:border-slate-400'}
          ${uploading ? 'opacity-50 cursor-not-allowed' : ''}
        `}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => !uploading && fileInputRef.current?.click()}
      >
        <input
          type="file"
          ref={fileInputRef}
          onChange={(e) => handleFileSelect(e.target.files?.[0])}
          accept={accept}
          className="hidden"
        />
        
        <div className="flex flex-col items-center gap-2 text-center">
          {uploading ? (
            <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
          ) : (
            <Upload className="h-8 w-8 text-slate-400" />
          )}
          <div>
            <p className="text-sm font-medium text-slate-700">
              {uploading ? 'Uploading...' : 'Click or drag file to upload'}
            </p>
            <p className="text-xs text-slate-500 mt-1">
              {accept.replace(/\./g, '').toUpperCase()} • up to 20MB
            </p>
          </div>
        </div>
      </div>

      {value && (
        <div className="flex justify-end">
          <Button type="button" variant="ghost" size="sm" onClick={handleClear}
                  data-testid="settings-file-clear">
            <X className="h-4 w-4 mr-1.5" /> Remove
          </Button>
        </div>
      )}

      {/* Preview */}
      {value && (
        <div className="mt-2 p-3 bg-slate-50 rounded-lg border">
          {isVideoUpload ? (
            <video 
              src={value} 
              className={`${previewSize} rounded border bg-black`}
              controls
              muted
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          ) : (
            <div className="flex items-center gap-3">
              <img 
                src={value} 
                alt="Preview" 
                className={`${previewSize} object-contain rounded border bg-white`}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className="hidden items-center gap-2 text-sm text-red-500" style={{display: 'none'}}>
                <AlertCircle className="h-4 w-4" />
                <span>Failed to load image</span>
              </div>
            </div>
          )}
          <div className="flex items-center gap-2 mt-2">
            <p className="text-xs text-slate-500 truncate flex-1">{value}</p>
            <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
          </div>
        </div>
      )}
    </div>
  );
};

const SystemSettings = () => {
  const queryClient = useQueryClient();
  const [showSecrets, setShowSecrets] = useState({});
  const [testingService, setTestingService] = useState(null);
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [sendingTestEmail, setSendingTestEmail] = useState(false);

  // Fetch current settings
  const { data: settings, isLoading, error } = useQuery({
    queryKey: ['system-settings'],
    queryFn: () => systemAPI.getSettings().then(r => r.data),
  });

  // Update settings mutation
  const updateSettingsMutation = useMutation({
    mutationFn: ({ section, data }) => systemAPI.updateSettings(section, data),
    onSuccess: () => {
      toast.success('Settings saved successfully!');
      queryClient.invalidateQueries(['system-settings']);
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed to save settings'))
  });

  // Test connection mutation
  const testConnectionMutation = useMutation({
    mutationFn: async (service) => {
      // Use specific endpoints for telegram and whatsapp
      if (service === 'telegram') {
        return systemAPI.testTelegram();
      }
      if (service === 'whatsapp') {
        return systemAPI.testWhatsApp();
      }
      return systemAPI.testConnection(service);
    },
    onSuccess: (response, service) => {
      toast.success(response.data?.message || `${service.toUpperCase()} connection successful!`);
      setTestingService(null);
    },
    onError: (err, service) => {
      toast.error(`${service.toUpperCase()} test failed: ${getErrorMessage(err, 'Connection error')}`);
      setTestingService(null);
    }
  });

  // Send test email function
  const handleSendTestEmail = async () => {
    if (!testEmailAddress) {
      toast.error('Please enter an email address');
      return;
    }
    setSendingTestEmail(true);
    try {
      const response = await api.post('/admin/system/test-email', { to_email: testEmailAddress });
      toast.success(response.data?.message || 'Test email sent successfully!');
    } catch (err) {
      const errorDetail = getErrorMessage(err, 'Failed to send test email');
      const statusCode = err.response?.status;
      
      // Show specific messages based on error type
      if (statusCode === 401 || errorDetail.toLowerCase().includes('password') || errorDetail.toLowerCase().includes('app password')) {
        toast.error('Gmail App Password Required', {
          description: (
            <div>
              <p>Gmail requires an App Password, not your regular password.</p>
              <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="text-blue-500 underline">
                Create App Password →
              </a>
            </div>
          ),
          duration: 10000,
        });
      } else if (errorDetail.toLowerCase().includes('timed out') || errorDetail.toLowerCase().includes('blocking')) {
        toast.error('SMTP connection blocked', {
          description: 'Your server may be blocking outbound email. Consider using SendGrid or Mailgun API.',
          duration: 8000,
        });
      } else {
        toast.error(errorDetail);
      }
    } finally {
      setSendingTestEmail(false);
    }
  };

  const [formData, setFormData] = useState({
    // SMTP Settings
    smtp_enabled: false,
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    smtp_from_email: '',
    smtp_from_name: 'EventFlow',
    smtp_use_tls: true,
    smtp_use_ssl: false,
    smtp_provider: 'custom',
    
    // SMS Settings (Twilio)
    sms_enabled: false,
    sms_provider: 'twilio',
    twilio_account_sid: '',
    twilio_auth_token: '',
    twilio_phone_number: '',
    
    // Telegram Settings
    telegram_enabled: false,
    telegram_bot_token: '',
    telegram_chat_id: '',
    
    // WhatsApp Business API Settings
    whatsapp_enabled: false,
    whatsapp_provider: 'meta',
    whatsapp_number: '',
    whatsapp_phone_id: '',
    whatsapp_business_id: '',
    whatsapp_access_token: '',
    whatsapp_verify_token: '',
    whatsapp_360_api_key: '',
    whatsapp_api_key: '',
    whatsapp_api_endpoint: '',
    
    // AI Settings
    ai_enabled: true,
    gemini_api_key: '',
    openai_api_key: '',
    
    // Payment Settings - Multiple Gateways
    payments_enabled: true,
    payment_provider: 'stripe',
    // Stripe
    stripe_publishable_key: '',
    stripe_secret_key: '',
    stripe_webhook_secret: '',
    // PayPal
    paypal_client_id: '',
    paypal_client_secret: '',
    paypal_mode: 'sandbox',
    // Razorpay
    razorpay_key_id: '',
    razorpay_key_secret: '',
    // Flutterwave
    flutterwave_public_key: '',
    flutterwave_secret_key: '',
    flutterwave_encryption_key: '',
    // Paystack
    paystack_public_key: '',
    paystack_secret_key: '',
    // Square
    square_app_id: '',
    square_access_token: '',
    square_location_id: '',
    // Mollie
    mollie_api_key: '',
    // Custom Gateway
    custom_gateway_name: '',
    custom_gateway_endpoint: '',
    custom_gateway_api_key: '',
    custom_gateway_api_secret: '',
    
    // General
    app_name: 'EventFlow',
    app_url: '',
    support_email: '',
    logo_url: '',
    favicon_url: '',
    splash_logo_url: '',
    
    // Landing Page Hero Settings
    hero_type: 'gradient', // 'gradient', 'image', 'video', 'color'
    hero_gradient_from: '#3B82F6',
    hero_gradient_to: '#6366F1',
    hero_color: '#3B82F6',
    hero_image_url: '',
    hero_video_url: '',
    hero_overlay_opacity: 20,
    hero_title: 'EventFlow',
    hero_subtitle: 'AI-Native Event Management Platform',
    hero_text_color: '#FFFFFF',
  });

  // Update form when settings load - flatten nested structure
  React.useEffect(() => {
    if (settings) {
      // Flatten the nested settings object
      const flatSettings = {};
      
      // SMTP settings
      if (settings.smtp) {
        Object.entries(settings.smtp).forEach(([key, value]) => {
          flatSettings[key] = value;
        });
      }
      
      // SMS settings
      if (settings.sms) {
        Object.entries(settings.sms).forEach(([key, value]) => {
          flatSettings[key] = value;
        });
      }
      
      // Telegram settings
      if (settings.telegram) {
        Object.entries(settings.telegram).forEach(([key, value]) => {
          flatSettings[key] = value;
        });
      }
      
      // WhatsApp settings
      if (settings.whatsapp) {
        Object.entries(settings.whatsapp).forEach(([key, value]) => {
          flatSettings[key] = value;
        });
      }
      
      // AI settings
      if (settings.ai) {
        Object.entries(settings.ai).forEach(([key, value]) => {
          flatSettings[key] = value;
        });
      }
      
      // Payment settings
      if (settings.payments) {
        Object.entries(settings.payments).forEach(([key, value]) => {
          flatSettings[key] = value;
        });
      }
      
      // General settings
      if (settings.general) {
        Object.entries(settings.general).forEach(([key, value]) => {
          flatSettings[key] = value;
        });
      }
      
      // Landing page settings
      if (settings.landing) {
        Object.entries(settings.landing).forEach(([key, value]) => {
          flatSettings[key] = value;
        });
      }
      
      setFormData(prev => ({ ...prev, ...flatSettings }));
    }
  }, [settings]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Per-section field allow-lists. Fixes the cross-section corruption bug
  // where saving one tab was overwriting sibling sub-documents in Mongo
  // (e.g. saving Stripe was writing SMTP host into payments.host, then
  // subsequent loads would reset the Stripe fields).
  const SECTION_FIELDS = {
    smtp: ['smtp_enabled', 'smtp_provider', 'smtp_host', 'smtp_port',
            'smtp_username', 'smtp_password', 'smtp_from_email',
            'smtp_from_name', 'smtp_use_tls', 'smtp_use_ssl',
            'smtp_use_starttls', 'smtp_reply_to',
            'sendgrid_api_key', 'mailgun_api_key',
            'mailgun_domain', 'resend_api_key'],
    sms: ['sms_enabled', 'sms_provider', 'twilio_account_sid',
            'twilio_auth_token', 'twilio_from_number', 'sms_from_name',
            'africas_talking_username', 'africas_talking_api_key',
            'vonage_api_key', 'vonage_api_secret'],
    telegram: ['telegram_bot_token', 'telegram_default_chat_id'],
    whatsapp: ['whatsapp_provider', 'whatsapp_phone_number_id',
                'whatsapp_access_token', 'whatsapp_business_account_id',
                'openwa_api_url', 'openwa_api_key', 'openwa_session'],
    ai: ['ai_provider', 'openai_api_key', 'openai_model',
          'gemini_api_key', 'gemini_model', 'anthropic_api_key',
          'anthropic_model', 'emergent_llm_key'],
    payments: ['payment_provider', 'payments_enabled', 'currency',
                'stripe_publishable_key', 'stripe_secret_key',
                'stripe_webhook_secret',
                'paypal_client_id', 'paypal_client_secret', 'paypal_mode',
                'square_app_id', 'square_access_token', 'square_location_id',
                'mollie_api_key',
                'custom_gateway_name', 'custom_gateway_endpoint',
                'custom_gateway_api_key', 'custom_gateway_api_secret'],
    general: ['site_name', 'site_url', 'timezone', 'locale',
               'default_language', 'default_currency', 'organisation_name',
               'organisation_email', 'organisation_phone', 'support_email'],
    landing: ['landing_hero_title', 'landing_hero_subtitle',
               'landing_hero_cta', 'landing_hero_image_url',
               'landing_show_stats', 'landing_show_testimonials',
               'landing_footer_text'],
  };

  const handleSave = (section) => {
    const allow = SECTION_FIELDS[section];
    // If we have an allow-list, filter to just those fields.
    // Fallback: pass everything (safety net for any section not listed).
    const scoped = allow
      ? Object.fromEntries(
          Object.entries(formData).filter(([k]) => allow.includes(k)))
      : formData;
    updateSettingsMutation.mutate({ section, data: scoped });
  };

  // Show error state if settings can't be loaded
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <XCircle className="h-12 w-12 text-red-400" />
        <p className="text-slate-400">Failed to load settings. Are you a Super Admin?</p>
        <p className="text-sm text-slate-500">{error.response?.data?.detail || error.message}</p>
      </div>
    );
  }

  const handleTest = (service) => {
    setTestingService(service);
    testConnectionMutation.mutate(service);
  };

  const toggleShowSecret = (field) => {
    setShowSecrets(prev => ({ ...prev, [field]: !prev[field] }));
  };

  // Helper to render SecretInput with current state
  const renderSecretInput = (field, value, placeholder) => (
    <SecretInput
      field={field}
      value={value}
      placeholder={placeholder}
      showSecrets={showSecrets}
      onToggle={toggleShowSecret}
      onChange={handleChange}
    />
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto p-6" data-testid="system-settings">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Shield className="h-8 w-8 text-indigo-600" />
            System Settings
          </h1>
          <p className="text-slate-500 mt-1">Configure SMTP, SMS, and integration settings</p>
        </div>
        <Badge variant="outline" className="text-indigo-600 border-indigo-600">
          Super Admin
        </Badge>
      </div>

      <Tabs defaultValue="smtp" className="space-y-6">
        <TabsList className="grid grid-cols-7 w-full">
          <TabsTrigger value="smtp" className="flex items-center gap-2">
            <Mail className="h-4 w-4" />
            <span className="hidden sm:inline">Email</span>
          </TabsTrigger>
          <TabsTrigger value="sms" className="flex items-center gap-2">
            <Smartphone className="h-4 w-4" />
            <span className="hidden sm:inline">SMS</span>
          </TabsTrigger>
          <TabsTrigger value="telegram" className="flex items-center gap-2">
            <Send className="h-4 w-4" />
            <span className="hidden sm:inline">Telegram</span>
          </TabsTrigger>
          <TabsTrigger value="whatsapp" className="flex items-center gap-2">
            <MessageCircle className="h-4 w-4" />
            <span className="hidden sm:inline">WhatsApp</span>
          </TabsTrigger>
          <TabsTrigger value="ai" className="flex items-center gap-2">
            <Brain className="h-4 w-4" />
            <span className="hidden sm:inline">AI</span>
          </TabsTrigger>
          <TabsTrigger value="payments" className="flex items-center gap-2">
            <CreditCard className="h-4 w-4" />
            <span className="hidden sm:inline">Pay</span>
          </TabsTrigger>
          <TabsTrigger value="currency" className="flex items-center gap-2">
            <Globe className="h-4 w-4" />
            <span className="hidden sm:inline">Currency</span>
          </TabsTrigger>
          <TabsTrigger value="general" className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            <span className="hidden sm:inline">General</span>
          </TabsTrigger>
          <TabsTrigger value="modules" className="flex items-center gap-2">
            <Eye className="h-4 w-4" />
            <span className="hidden sm:inline">Modules</span>
          </TabsTrigger>
          <TabsTrigger value="landing" className="flex items-center gap-2">
            <Layout className="h-4 w-4" />
            <span className="hidden sm:inline">Landing</span>
          </TabsTrigger>
          <TabsTrigger value="landing-pages" className="flex items-center gap-2">
            <Palette className="h-4 w-4" />
            <span className="hidden sm:inline">Module Pages</span>
          </TabsTrigger>
          <TabsTrigger value="appearance" className="flex items-center gap-2"
                       data-testid="settings-tab-appearance">
            <Image className="h-4 w-4" />
            <span className="hidden sm:inline">Appearance</span>
          </TabsTrigger>
          <TabsTrigger value="rbac" className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            <span className="hidden sm:inline">RBAC</span>
          </TabsTrigger>
          <TabsTrigger value="finance" className="flex items-center gap-2">
            <CreditCard className="h-4 w-4" />
            <span className="hidden sm:inline">Finance</span>
          </TabsTrigger>
        </TabsList>

        {/* Site appearance — hero image, background video and words per public page */}
        <TabsContent value="appearance" className="space-y-4">
          <Card>
            <CardHeader className="border-b">
              <CardTitle className="flex items-center gap-2 text-xl">
                <Image className="h-5 w-5" />
                Site appearance
              </CardTitle>
              <CardDescription>
                The photograph, optional background video and words a visitor sees first —
                on the homepage before login and on every module's public page.
                Changes go live immediately.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <SiteAppearancePanel />
            </CardContent>
          </Card>
        </TabsContent>

        {/* SMTP Settings - Clean Two-Column Layout */}
        <TabsContent value="smtp">
          <Card>
            <CardHeader className="border-b">
              <CardTitle className="flex items-center gap-2 text-xl">
                <Mail className="h-5 w-5" />
                Email Setting
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              {/* Clean two-column form layout */}
              <div className="space-y-4">
                {/* Email Engine / Provider */}
                <div className="grid grid-cols-2 sm:grid-cols-3 items-center gap-4">
                  <Label className="text-right font-medium">Email Engine</Label>
                  <div className="col-span-2">
                    <Select 
                      value={formData.smtp_provider || 'smtp'} 
                      onValueChange={(v) => {
                        handleChange('smtp_provider', v);
                        // Auto-fill SMTP settings based on provider
                        if (v === 'gmail') {
                          handleChange('smtp_host', 'smtp.gmail.com');
                          handleChange('smtp_port', 587);
                          handleChange('smtp_use_tls', true);
                          handleChange('smtp_use_ssl', false);
                        } else if (v === 'outlook') {
                          handleChange('smtp_host', 'smtp.office365.com');
                          handleChange('smtp_port', 587);
                          handleChange('smtp_use_tls', true);
                          handleChange('smtp_use_ssl', false);
                        } else if (v === 'yahoo') {
                          handleChange('smtp_host', 'smtp.mail.yahoo.com');
                          handleChange('smtp_port', 587);
                          handleChange('smtp_use_tls', true);
                          handleChange('smtp_use_ssl', false);
                        } else if (v === 'zoho') {
                          handleChange('smtp_host', 'smtp.zoho.com');
                          handleChange('smtp_port', 587);
                          handleChange('smtp_use_tls', true);
                          handleChange('smtp_use_ssl', false);
                        }
                      }}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select provider" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="gmail">Gmail</SelectItem>
                        <SelectItem value="outlook">Outlook / Office 365</SelectItem>
                        <SelectItem value="yahoo">Yahoo Mail</SelectItem>
                        <SelectItem value="zoho">Zoho Mail</SelectItem>
                        <SelectItem value="smtp">Custom SMTP</SelectItem>
                        <SelectItem value="sendgrid">SendGrid API</SelectItem>
                        <SelectItem value="mailgun">Mailgun API</SelectItem>
                        <SelectItem value="ses">Amazon SES</SelectItem>
                      </SelectContent>
                    </Select>
                    {formData.smtp_provider === 'gmail' && (
                      <p className="text-xs text-amber-600 mt-1">
                        ⚠️ Gmail requires an <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="underline">App Password</a> (not your regular password)
                      </p>
                    )}
                  </div>
                </div>

                {/* SMTP Username */}
                <div className="grid grid-cols-2 sm:grid-cols-3 items-center gap-4">
                  <Label className="text-right font-medium">SMTP Username</Label>
                  <div className="col-span-2">
                    <Input
                      value={formData.smtp_username}
                      onChange={(e) => handleChange('smtp_username', e.target.value)}
                      placeholder="your-email@domain.com"
                      data-testid="smtp-username"
                    />
                  </div>
                </div>

                {/* SMTP Password */}
                <div className="grid grid-cols-2 sm:grid-cols-3 items-center gap-4">
                  <Label className="text-right font-medium">SMTP Password</Label>
                  <div className="col-span-2">
                    {renderSecretInput("smtp_password", formData.smtp_password, "••••••••")}
                  </div>
                </div>

                {/* SMTP Server */}
                <div className="grid grid-cols-2 sm:grid-cols-3 items-center gap-4">
                  <Label className="text-right font-medium">SMTP Server</Label>
                  <div className="col-span-2">
                    <Input
                      value={formData.smtp_host}
                      onChange={(e) => handleChange('smtp_host', e.target.value)}
                      placeholder="smtp.gmail.com"
                      data-testid="smtp-server"
                    />
                  </div>
                </div>

                {/* SMTP Port */}
                <div className="grid grid-cols-2 sm:grid-cols-3 items-center gap-4">
                  <Label className="text-right font-medium">SMTP Port</Label>
                  <div className="col-span-2">
                    <Input
                      type="number"
                      value={formData.smtp_port}
                      onChange={(e) => handleChange('smtp_port', parseInt(e.target.value) || 587)}
                      placeholder="587"
                      data-testid="smtp-port"
                    />
                  </div>
                </div>

                {/* SMTP Security — STARTTLS (587) · SSL (465) · none (25) */}
                <div className="grid grid-cols-2 sm:grid-cols-3 items-center gap-4">
                  <Label className="text-right font-medium">SMTP Security</Label>
                  <div className="col-span-2">
                    <Select
                      value={formData.smtp_use_ssl ? 'ssl' : (formData.smtp_use_tls ? 'tls' : 'none')}
                      onValueChange={(v) => {
                        handleChange('smtp_use_tls', v === 'tls');
                        handleChange('smtp_use_ssl', v === 'ssl');
                        if (v === 'ssl' && Number(formData.smtp_port) === 587) handleChange('smtp_port', 465);
                        if (v === 'tls' && Number(formData.smtp_port) === 465) handleChange('smtp_port', 587);
                      }}
                    >
                      <SelectTrigger className="w-full" data-testid="smtp-security-select">
                        <SelectValue placeholder="Select security" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="tls">STARTTLS — port 587 (most providers)</SelectItem>
                        <SelectItem value="ssl">SSL/TLS — port 465</SelectItem>
                        <SelectItem value="none">None — plain relay, port 25</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500 mt-1">
                      Gmail, Workspace, Zoho and Outlook all use STARTTLS on port 587. With
                      Gmail you must paste an <b>App Password</b>, not your normal password.
                    </p>
                  </div>
                </div>

                {/* SMTP Auth */}
                <div className="grid grid-cols-2 sm:grid-cols-3 items-center gap-4">
                  <Label className="text-right font-medium">SMTP Auth</Label>
                  <div className="col-span-2">
                    <Select 
                      value={formData.smtp_enabled ? 'on' : 'off'} 
                      onValueChange={(v) => handleChange('smtp_enabled', v === 'on')}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Enable/Disable" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="on">On</SelectItem>
                        <SelectItem value="off">Off</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* From Email */}
                <div className="grid grid-cols-2 sm:grid-cols-3 items-center gap-4">
                  <Label className="text-right font-medium">From Email</Label>
                  <div className="col-span-2">
                    <Input
                      value={formData.smtp_from_email}
                      onChange={(e) => handleChange('smtp_from_email', e.target.value)}
                      placeholder="noreply@yourdomain.com"
                      data-testid="smtp-from-email"
                    />
                  </div>
                </div>

                {/* From Name */}
                <div className="grid grid-cols-2 sm:grid-cols-3 items-center gap-4">
                  <Label className="text-right font-medium">From Name</Label>
                  <div className="col-span-2">
                    <Input
                      value={formData.smtp_from_name}
                      onChange={(e) => handleChange('smtp_from_name', e.target.value)}
                      placeholder="EventFlow"
                      data-testid="smtp-from-name"
                    />
                  </div>
                </div>
              </div>

              {/* Test Email Section */}
              <div className="mt-8 p-4 bg-slate-50 rounded-lg space-y-3">
                <h4 className="font-medium text-sm">Send Test Email</h4>
                <div className="flex gap-2">
                  <Input
                    type="email"
                    value={testEmailAddress}
                    onChange={(e) => setTestEmailAddress(e.target.value)}
                    placeholder="Enter your email address"
                    className="flex-1"
                    data-testid="test-email-input"
                  />
                  <Button
                    variant="secondary"
                    onClick={handleSendTestEmail}
                    disabled={sendingTestEmail || !formData.smtp_enabled || !formData.smtp_host || !testEmailAddress}
                    data-testid="send-test-email-btn"
                  >
                    {sendingTestEmail ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Mail className="h-4 w-4 mr-2" />
                    )}
                    Send Test
                  </Button>
                </div>
              </div>

              <EmailOutboxCard />

              {/* Save Button */}
              <div className="flex justify-end pt-6 mt-6 border-t">
                <Button 
                  onClick={() => handleSave('smtp')} 
                  disabled={updateSettingsMutation.isPending}
                  className="px-8"
                >
                  {updateSettingsMutation.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Save Settings
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SMS Settings */}
        <TabsContent value="sms">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Smartphone className="h-5 w-5" />
                    SMS Configuration
                  </CardTitle>
                  <CardDescription>Configure SMS provider for notifications</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Label htmlFor="sms-enabled">Enabled</Label>
                  <Switch
                    id="sms-enabled"
                    checked={formData.sms_enabled}
                    onCheckedChange={(v) => handleChange('sms_enabled', v)}
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>SMS Provider</Label>
                <Select value={formData.sms_provider} onValueChange={(v) => handleChange('sms_provider', v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="twilio">Twilio</SelectItem>
                    <SelectItem value="nexmo">Vonage (Nexmo)</SelectItem>
                    <SelectItem value="sns">AWS SNS</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.sms_provider === 'twilio' && (
                <>
                  <div className="space-y-2">
                    <Label>Twilio Account SID</Label>
                    {renderSecretInput("twilio_account_sid", formData.twilio_account_sid, "ACxxxxxxxxxxxxxxx")}
                  </div>
                  <div className="space-y-2">
                    <Label>Twilio Auth Token</Label>
                    {renderSecretInput("twilio_auth_token", formData.twilio_auth_token, "Auth token")}
                  </div>
                  <div className="space-y-2">
                    <Label>Twilio Phone Number</Label>
                    <Input
                      value={formData.twilio_phone_number}
                      onChange={(e) => handleChange('twilio_phone_number', e.target.value)}
                      placeholder="+1234567890"
                    />
                  </div>
                </>
              )}

              <Alert>
                <MessageSquare className="h-4 w-4" />
                <AlertDescription>
                  SMS is used for check-in notifications, reminders, and emergency alerts.
                </AlertDescription>
              </Alert>

              <div className="flex justify-between pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => handleTest('sms')}
                  disabled={testingService === 'sms' || !formData.twilio_account_sid}
                >
                  {testingService === 'sms' ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <TestTube className="h-4 w-4 mr-2" />
                  )}
                  Test SMS
                </Button>
                <Button onClick={() => handleSave('sms')} disabled={updateSettingsMutation.isPending}>
                  <Save className="h-4 w-4 mr-2" />
                  Save SMS Settings
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Telegram Settings */}
        <TabsContent value="telegram">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Send className="h-5 w-5" />
                    Telegram Bot Configuration
                  </CardTitle>
                  <CardDescription>Configure Telegram bot for channel broadcasts</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Label htmlFor="telegram-enabled">Enabled</Label>
                  <Switch
                    id="telegram-enabled"
                    checked={formData.telegram_enabled}
                    onCheckedChange={(v) => handleChange('telegram_enabled', v)}
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Bot Token</Label>
                {renderSecretInput("telegram_bot_token", formData.telegram_bot_token, "123456789:ABCdefGHI...")}
                <p className="text-xs text-slate-500">Get from @BotFather on Telegram</p>
              </div>

              <div className="space-y-2">
                <Label>Default Chat/Channel ID</Label>
                <Input
                  value={formData.telegram_chat_id}
                  onChange={(e) => handleChange('telegram_chat_id', e.target.value)}
                  placeholder="-1001234567890"
                />
                <p className="text-xs text-slate-500">Channel or group ID for broadcasts. Use @username or numeric ID.</p>
              </div>

              <Alert>
                <Send className="h-4 w-4" />
                <AlertDescription>
                  <strong>Setup steps:</strong> 1) Create bot via @BotFather, 2) Add bot to your channel as admin, 3) Get channel ID using @userinfobot
                </AlertDescription>
              </Alert>

              <div className="flex justify-between pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => handleTest('telegram')}
                  disabled={testingService === 'telegram' || !formData.telegram_bot_token}
                >
                  {testingService === 'telegram' ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <TestTube className="h-4 w-4 mr-2" />
                  )}
                  Test Telegram
                </Button>
                <Button onClick={() => handleSave('telegram')} disabled={updateSettingsMutation.isPending}>
                  <Save className="h-4 w-4 mr-2" />
                  Save Telegram Settings
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* WhatsApp Settings — DEPRECATED, redirected to /admin/whatsapp */}
        <TabsContent value="whatsapp">
          <Card>
            <CardHeader className="border-b">
              <CardTitle className="flex items-center gap-2 text-xl">
                <MessageCircle className="h-5 w-5" />
                WhatsApp Business API
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <Alert className="bg-emerald-50 border-emerald-200" data-testid="legacy-wa-redirect-banner">
                <MessageCircle className="h-4 w-4 text-emerald-700" />
                <AlertDescription className="text-emerald-900">
                  <p className="font-semibold mb-2">WhatsApp configuration has moved.</p>
                  <p className="text-sm mb-3">
                    All WhatsApp sends across the platform now route through the unified OpenWA channel — templates, broadcasts, opt-outs, analytics, and retry queue are all managed from one place.
                  </p>
                  <Button asChild className="bg-emerald-600 hover:bg-emerald-700">
                    <a href="/admin/whatsapp" data-testid="open-whatsapp-admin-btn">
                      Open WhatsApp Admin →
                    </a>
                  </Button>
                  <p className="text-xs text-emerald-800/80 mt-3">
                    The legacy Meta / Twilio / 360dialog config below is kept read-only for back-compat. New sends use OpenWA by default.
                  </p>
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </TabsContent>

        {/* AI Settings */}
        <TabsContent value="ai">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Brain className="h-5 w-5" />
                    AI Services Configuration
                  </CardTitle>
                  <CardDescription>Configure AI providers for research and content generation</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Label htmlFor="ai-enabled">Enabled</Label>
                  <Switch
                    id="ai-enabled"
                    checked={formData.ai_enabled}
                    onCheckedChange={(v) => handleChange('ai_enabled', v)}
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Google Gemini API Key</Label>
                {renderSecretInput("gemini_api_key", formData.gemini_api_key, "AIzaSy...")}
                <p className="text-xs text-slate-500">Used for AI Research Tool and content generation</p>
              </div>

              <div className="space-y-2">
                <Label>OpenAI API Key (Optional)</Label>
                {renderSecretInput("openai_api_key", formData.openai_api_key, "sk-...")}
                <p className="text-xs text-slate-500">Optional fallback for AI features</p>
              </div>

              <div className="flex justify-between pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => handleTest('ai')}
                  disabled={testingService === 'ai' || !formData.gemini_api_key}
                >
                  {testingService === 'ai' ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <TestTube className="h-4 w-4 mr-2" />
                  )}
                  Test AI Connection
                </Button>
                <Button onClick={() => handleSave('ai')} disabled={updateSettingsMutation.isPending}>
                  <Save className="h-4 w-4 mr-2" />
                  Save AI Settings
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payment Settings - Multiple Gateways */}
        <TabsContent value="payments">
          <Card>
            <CardHeader className="border-b">
              <CardTitle className="flex items-center gap-2 text-xl">
                <CreditCard className="h-5 w-5" />
                Payment Gateways
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="space-y-4">
                {/* Enable/Disable */}
                <div className="grid grid-cols-2 sm:grid-cols-3 items-center gap-4">
                  <Label className="text-right font-medium">Payments</Label>
                  <div className="col-span-2">
                    <Select 
                      value={formData.payments_enabled ? 'on' : 'off'} 
                      onValueChange={(v) => handleChange('payments_enabled', v === 'on')}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="on">Enabled</SelectItem>
                        <SelectItem value="off">Disabled</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Primary Payment Provider */}
                <div className="grid grid-cols-2 sm:grid-cols-3 items-center gap-4">
                  <Label className="text-right font-medium">Primary Gateway</Label>
                  <div className="col-span-2">
                    <Select 
                      value={formData.payment_provider || 'stripe'} 
                      onValueChange={(v) => handleChange('payment_provider', v)}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="stripe">Stripe</SelectItem>
                        <SelectItem value="paypal">PayPal</SelectItem>
                        <SelectItem value="razorpay">Razorpay</SelectItem>
                        <SelectItem value="flutterwave">Flutterwave</SelectItem>
                        <SelectItem value="paystack">Paystack</SelectItem>
                        <SelectItem value="square">Square</SelectItem>
                        <SelectItem value="mollie">Mollie</SelectItem>
                        <SelectItem value="custom">Custom Gateway</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Stripe Settings */}
                {(formData.payment_provider === 'stripe' || !formData.payment_provider) && (
                  <>
                    <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                      <h4 className="font-medium mb-3 flex items-center gap-2">
                        <CreditCard className="h-4 w-4" /> Stripe Configuration
                      </h4>
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 sm:grid-cols-3 items-center gap-4">
                          <Label className="text-right text-sm">Publishable Key</Label>
                          <div className="col-span-2">
                            {renderSecretInput("stripe_publishable_key", formData.stripe_publishable_key, "pk_live_...")}
                          </div>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 items-center gap-4">
                          <Label className="text-right text-sm">Secret Key</Label>
                          <div className="col-span-2">
                            {renderSecretInput("stripe_secret_key", formData.stripe_secret_key, "sk_live_...")}
                          </div>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 items-center gap-4">
                          <Label className="text-right text-sm">Webhook Secret</Label>
                          <div className="col-span-2">
                            {renderSecretInput("stripe_webhook_secret", formData.stripe_webhook_secret, "whsec_...")}
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* PayPal Settings */}
                {formData.payment_provider === 'paypal' && (
                  <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-medium mb-3">PayPal Configuration</h4>
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 sm:grid-cols-3 items-center gap-4">
                        <Label className="text-right text-sm">Client ID</Label>
                        <div className="col-span-2">
                          {renderSecretInput("paypal_client_id", formData.paypal_client_id, "AXxxxxxxxx...")}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 items-center gap-4">
                        <Label className="text-right text-sm">Client Secret</Label>
                        <div className="col-span-2">
                          {renderSecretInput("paypal_client_secret", formData.paypal_client_secret, "ELxxxxxxxx...")}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 items-center gap-4">
                        <Label className="text-right text-sm">Mode</Label>
                        <div className="col-span-2">
                          <Select 
                            value={formData.paypal_mode || 'sandbox'} 
                            onValueChange={(v) => handleChange('paypal_mode', v)}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="sandbox">Sandbox (Testing)</SelectItem>
                              <SelectItem value="live">Live (Production)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Razorpay Settings */}
                {formData.payment_provider === 'razorpay' && (
                  <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-medium mb-3">Razorpay Configuration</h4>
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 sm:grid-cols-3 items-center gap-4">
                        <Label className="text-right text-sm">Key ID</Label>
                        <div className="col-span-2">
                          {renderSecretInput("razorpay_key_id", formData.razorpay_key_id, "rzp_live_...")}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 items-center gap-4">
                        <Label className="text-right text-sm">Key Secret</Label>
                        <div className="col-span-2">
                          {renderSecretInput("razorpay_key_secret", formData.razorpay_key_secret, "Your secret key")}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Flutterwave Settings */}
                {formData.payment_provider === 'flutterwave' && (
                  <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-medium mb-3">Flutterwave Configuration</h4>
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 sm:grid-cols-3 items-center gap-4">
                        <Label className="text-right text-sm">Public Key</Label>
                        <div className="col-span-2">
                          {renderSecretInput("flutterwave_public_key", formData.flutterwave_public_key, "FLWPUBK_...")}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 items-center gap-4">
                        <Label className="text-right text-sm">Secret Key</Label>
                        <div className="col-span-2">
                          {renderSecretInput("flutterwave_secret_key", formData.flutterwave_secret_key, "FLWSECK_...")}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 items-center gap-4">
                        <Label className="text-right text-sm">Encryption Key</Label>
                        <div className="col-span-2">
                          {renderSecretInput("flutterwave_encryption_key", formData.flutterwave_encryption_key, "Your encryption key")}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Paystack Settings */}
                {formData.payment_provider === 'paystack' && (
                  <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-medium mb-3">Paystack Configuration</h4>
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 sm:grid-cols-3 items-center gap-4">
                        <Label className="text-right text-sm">Public Key</Label>
                        <div className="col-span-2">
                          {renderSecretInput("paystack_public_key", formData.paystack_public_key, "pk_live_...")}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 items-center gap-4">
                        <Label className="text-right text-sm">Secret Key</Label>
                        <div className="col-span-2">
                          {renderSecretInput("paystack_secret_key", formData.paystack_secret_key, "sk_live_...")}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Square Settings */}
                {formData.payment_provider === 'square' && (
                  <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-medium mb-3">Square Configuration</h4>
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 sm:grid-cols-3 items-center gap-4">
                        <Label className="text-right text-sm">Application ID</Label>
                        <div className="col-span-2">
                          {renderSecretInput("square_app_id", formData.square_app_id, "sq0idp-...")}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 items-center gap-4">
                        <Label className="text-right text-sm">Access Token</Label>
                        <div className="col-span-2">
                          {renderSecretInput("square_access_token", formData.square_access_token, "EAAAxxxxxxxx...")}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 items-center gap-4">
                        <Label className="text-right text-sm">Location ID</Label>
                        <div className="col-span-2">
                          <Input
                            value={formData.square_location_id || ''}
                            onChange={(e) => handleChange('square_location_id', e.target.value)}
                            placeholder="Your location ID"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Mollie Settings */}
                {formData.payment_provider === 'mollie' && (
                  <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-medium mb-3">Mollie Configuration</h4>
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 sm:grid-cols-3 items-center gap-4">
                        <Label className="text-right text-sm">API Key</Label>
                        <div className="col-span-2">
                          {renderSecretInput("mollie_api_key", formData.mollie_api_key, "live_xxxxxxxx...")}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Custom Gateway Settings */}
                {formData.payment_provider === 'custom' && (
                  <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-medium mb-3">Custom Gateway Configuration</h4>
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 sm:grid-cols-3 items-center gap-4">
                        <Label className="text-right text-sm">Gateway Name</Label>
                        <div className="col-span-2">
                          <Input
                            value={formData.custom_gateway_name || ''}
                            onChange={(e) => handleChange('custom_gateway_name', e.target.value)}
                            placeholder="My Payment Gateway"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 items-center gap-4">
                        <Label className="text-right text-sm">API Endpoint</Label>
                        <div className="col-span-2">
                          <Input
                            value={formData.custom_gateway_endpoint || ''}
                            onChange={(e) => handleChange('custom_gateway_endpoint', e.target.value)}
                            placeholder="https://api.gateway.com/v1/payments"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 items-center gap-4">
                        <Label className="text-right text-sm">API Key</Label>
                        <div className="col-span-2">
                          {renderSecretInput("custom_gateway_api_key", formData.custom_gateway_api_key, "Your API key")}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 items-center gap-4">
                        <Label className="text-right text-sm">API Secret</Label>
                        <div className="col-span-2">
                          {renderSecretInput("custom_gateway_api_secret", formData.custom_gateway_api_secret, "Your API secret")}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Info */}
              <Alert className="mt-6 bg-blue-50 border-blue-200">
                <CreditCard className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-blue-800">
                  Payment gateways are used for processing ticket purchases, paid registrations, and merchandise sales.
                </AlertDescription>
              </Alert>

              {/* Save Button */}
              <div className="flex justify-end pt-6 mt-6 border-t">
                <Button onClick={() => handleSave('payments')} disabled={updateSettingsMutation.isPending}>
                  {updateSettingsMutation.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Save Payment Settings
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Currency Settings */}
        <TabsContent value="currency" className="space-y-4">
          <CurrencySettings />
          <CurrencyManager />
        </TabsContent>

        {/* General Settings */}
        <TabsContent value="general">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5" />
                General Settings
              </CardTitle>
              <CardDescription>Application-wide configuration and branding</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* App Identity */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-slate-700 border-b pb-2">App Identity</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Application Name</Label>
                    <Input
                      value={formData.app_name}
                      onChange={(e) => handleChange('app_name', e.target.value)}
                      placeholder="EventFlow"
                      data-testid="app-name-input"
                    />
                    <p className="text-xs text-slate-500">This name appears in the browser tab and headers</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Application URL</Label>
                    <Input
                      value={formData.app_url}
                      onChange={(e) => handleChange('app_url', e.target.value)}
                      placeholder="https://yourdomain.com"
                      data-testid="app-url-input"
                    />
                    <p className="text-xs text-slate-500">Your production domain URL</p>
                  </div>
                  <div className="space-y-2 col-span-2">
                    <Label>Tagline</Label>
                    <Input
                      value={formData.tagline || ''}
                      onChange={(e) => handleChange('tagline', e.target.value)}
                      placeholder="One platform for the ministry, its churches and its schools"
                      data-testid="app-tagline-input"
                    />
                    <p className="text-xs text-slate-500">Shown under your logo on the public homepage</p>
                  </div>
                </div>
              </div>

              {/* Branding */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-slate-700 border-b pb-2">Branding</h3>
                <div className="grid grid-cols-2 gap-4">
                  <FileUpload
                    label="Logo"
                    value={formData.logo_url}
                    onChange={(url) => handleChange('logo_url', url)}
                    fileType="logo"
                    accept=".png,.jpg,.jpeg,.svg,.webp"
                    previewSize="max-h-12"
                  />
                  <FileUpload
                    label="Favicon"
                    value={formData.favicon_url}
                    onChange={(url) => handleChange('favicon_url', url)}
                    fileType="favicon"
                    accept=".ico,.png,.svg"
                    previewSize="w-8 h-8"
                  />
                  <FileUpload
                    label="Splash tile"
                    value={formData.splash_logo_url}
                    onChange={(url) => handleChange('splash_logo_url', url)}
                    fileType="logo"
                    accept=".png,.jpg,.jpeg,.webp"
                    previewSize="w-16 h-16"
                  />
                </div>
                <p className="text-xs text-slate-500">
                  You can either upload a file or enter a URL. Recommended sizes: Logo 200x50px, Favicon 32x32px.
                  The splash tile is the square icon shown on the dark loading screen — use a light, opaque square
                  (512x512) with padding, since a transparent logo with dark lettering disappears on navy.
                </p>
              </div>

              {/* Contact */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-slate-700 border-b pb-2">Contact Information</h3>
                <div className="space-y-2">
                  <Label>Support Email</Label>
                  <Input
                    value={formData.support_email}
                    onChange={(e) => handleChange('support_email', e.target.value)}
                    placeholder="support@yourdomain.com"
                    data-testid="support-email-input"
                  />
                  <p className="text-xs text-slate-500">Email address shown to users for support requests</p>
                </div>
              </div>

              <div className="flex justify-end pt-4 border-t">
                <Button onClick={() => handleSave('general')} disabled={updateSettingsMutation.isPending}>
                  <Save className="h-4 w-4 mr-2" />
                  Save General Settings
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Module Visibility Settings */}
        <TabsContent value="modules">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5" />
                Module Visibility
              </CardTitle>
              <CardDescription>Control which modules are visible to the public</CardDescription>
            </CardHeader>
            <CardContent>
              <ModuleVisibilityManager />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Landing Page Settings */}
        <TabsContent value="landing">
          <Card data-testid="landing-hero-moved-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Layout className="h-5 w-5" />
                Homepage
              </CardTitle>
              <CardDescription>
                The homepage is now edited section by section — hero picture, wording,
                palette, what shows and in what order.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-slate-600 max-w-xl">
                The old hero fields on this tab no longer drive anything: they belonged to the
                previous homepage. Everything the visitor sees at <code className="text-xs">/</code>
                {' '}now lives in the Homepage editor, where you can upload a hero picture, switch
                between the light and dark palette, toggle sections on or off and reorder them.
              </p>
              <div className="flex flex-wrap gap-2">
                <Link to="/admin/landing">
                  <Button data-testid="landing-open-editor-btn">
                    Open the Homepage editor
                  </Button>
                </Link>
                <a href="/" target="_blank" rel="noreferrer">
                  <Button variant="outline" data-testid="landing-view-page-btn">
                    View the homepage
                  </Button>
                </a>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Module Landing Pages Settings */}
        <TabsContent value="landing-pages">
          <Card>
            <CardHeader className="border-b">
              <CardTitle className="flex items-center gap-2 text-xl">
                <Palette className="h-5 w-5" />
                Module Landing Pages
              </CardTitle>
              <CardDescription>
                Customize the landing pages for Events, Churches, Giving, Bible, and Campaigns
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <LandingPagesSettings />
            </CardContent>
          </Card>
        </TabsContent>

        {/* RBAC - Role Based Access Control */}
        <TabsContent value="rbac">
          <HierarchicalRoleAssignment />
        </TabsContent>

        {/* Hierarchical Finance Dashboard */}
        <TabsContent value="finance">
          <HierarchicalFinanceDashboard />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SystemSettings;
